/* eslint-disable react/prop-types */
/**
 * WeeklyLockList — top-of-page banner surfacing the biggest CFB blowouts
 * and NFL best-odds favorites for the upcoming week.  Deterministic
 * ESPN-odds-based ranking (no LLM) so the user can build the highest
 * win-probability parlay the night before kickoff.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Flame, Target, Copy, RefreshCw, ChevronDown, Mail, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sports-betting`;

const BUCKET_PRIORITY = {
  'Thu night':             1,
  'Fri night':             2,
  'Sat early':             3,
  'Sat afternoon':         4,
  'Sat night':             5,
  'Sun early (10am PT)':   6,
  'Sun 1pm ET':            7,
  'Sun late afternoon':    8,
  'Sun night':             9,
  'Mon night':             10,
};

function winProbColor(wp) {
  if (wp >= 90) return 'text-emerald-300';
  if (wp >= 80) return 'text-emerald-400';
  if (wp >= 70) return 'text-amber-300';
  return 'text-sky-300';
}

function PickRow({ p, league }) {
  const wpClass = winProbColor(p.win_prob);
  const copy = (text) => {
    try {
      navigator.clipboard.writeText(text);
      toast.success('Copied — paste into your sportsbook');
    } catch {
      toast.error('Could not copy');
    }
  };
  const favName = p.favorite?.name || p.favorite?.abbr || '?';
  const dogName = p.underdog?.name || p.underdog?.abbr || '?';
  const favHome = p.favorite?.home_away === 'home';
  const matchup = favHome
    ? `${dogName} @ ${favName}`
    : `${favName} @ ${dogName}`;
  return (
    <div className="flex items-center gap-2 py-2 border-b border-zinc-800/70 last:border-b-0"
         data-testid={`lock-${league}-${p.event_id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-sm font-semibold text-white truncate">
            {favName}
            {p.favorite?.rank && p.favorite.rank < 25 && (
              <span className="ml-1 text-[10px] text-amber-300">#{p.favorite.rank}</span>
            )}
          </span>
          <span className="text-xs font-mono text-red-300 font-bold">
            {p.details}
          </span>
        </div>
        <div className="text-[10px] text-zinc-500 truncate">
          {matchup} · {p.bucket} · {p.kickoff_pt}
        </div>
      </div>
      <div className="text-right">
        <div className={`text-sm font-bold font-mono ${wpClass}`}
             data-testid={`lock-wp-${p.event_id}`}>
          {p.win_prob}%
        </div>
        <div className="text-[9px] text-zinc-500">win prob</div>
      </div>
      <button
        onClick={() => copy(`${p.spread_selection_text} (${p.kickoff_pt})`)}
        className="shrink-0 p-1.5 rounded hover:bg-zinc-800 text-zinc-400 hover:text-amber-300"
        title="Copy selection"
        data-testid={`lock-copy-${p.event_id}`}
      >
        <Copy className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function Section({ title, icon, picks, league, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  // Group by day bucket for easier eye-scanning
  const grouped = useMemo(() => {
    const byBucket = {};
    for (const p of picks) {
      const b = p.bucket || 'Other';
      (byBucket[b] = byBucket[b] || []).push(p);
    }
    return Object.entries(byBucket)
      .sort((a, b) => (BUCKET_PRIORITY[a[0]] || 99) - (BUCKET_PRIORITY[b[0]] || 99));
  }, [picks]);
  return (
    <div className="rounded-xl bg-zinc-950/60 border border-zinc-800">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2.5 text-left"
        data-testid={`lock-section-${league}`}
      >
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-sm font-bold text-white">{title}</span>
          <span className="text-[11px] text-zinc-500">({picks.length})</span>
        </div>
        <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && (
        <div className="px-3 pb-2">
          {picks.length === 0 ? (
            <p className="text-xs text-zinc-500 py-3">
              No qualifying games in the next 7 days.
            </p>
          ) : grouped.map(([bucket, bucketPicks]) => (
            <div key={bucket} className="mb-1">
              <div className="text-[10px] uppercase tracking-wider text-amber-300/80 py-1 border-b border-amber-500/20">
                {bucket} <span className="text-zinc-500 normal-case">· {bucketPicks.length} pick{bucketPicks.length === 1 ? '' : 's'}</span>
              </div>
              {bucketPicks.map((p) => <PickRow key={p.event_id} p={p} league={league} />)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function WeeklyLockList({ only = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(true);
  const [emailOpen, setEmailOpen] = useState(false);
  const [settings, setSettings] = useState({ recipient_email: '', cfb_enabled: true, nfl_enabled: true });
  const [sending, setSending] = useState(null);  // null | 'cfb' | 'nfl' | 'both'

  const load = async () => {
    setLoading(true);
    try {
      const [locks, sets] = await Promise.all([
        axios.get(`${API}/weekly-locks?days=7`),
        axios.get(`${API}/lock-list-settings`),
      ]);
      setData(locks.data);
      setSettings(sets.data || settings);
    } catch (e) {
      toast.error(`Lock list failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      await axios.put(`${API}/lock-list-settings`, settings);
      toast.success('Email settings saved');
      setEmailOpen(false);
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const sendNow = async (league) => {
    setSending(league);
    try {
      // 2026-02-28 (user fix) — Send to WHAT'S CURRENTLY IN THE INPUT,
      // not whatever stale `recipient_email` was last saved to the DB.
      // Also auto-persist the current recipient + league toggles so a
      // forgetful admin isn't stuck re-saving before every send.
      const recipient = (settings.recipient_email || '').trim();
      if (!recipient) {
        toast.error('Enter a recipient email first');
        setSending(null);
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipient)) {
        toast.error('That email address looks invalid');
        setSending(null);
        return;
      }
      // Fire-and-forget auto-save so the next scheduled send also picks
      // up the newly-typed address.
      try { await axios.put(`${API}/lock-list-settings`, settings); } catch (_) {}

      const res = await axios.post(`${API}/lock-list-send-now`, {
        league,
        recipient_email: recipient,
      });
      // Backend returns either {cfb:{...}} or {nfl:{...}} depending on
      // the requested league.  Also surface skipped / error reasons so
      // the admin knows WHY it didn't ship.
      const parts = [];
      const problems = [];
      for (const key of ['cfb', 'nfl']) {
        const r = res.data?.[key];
        if (!r) continue;
        if (r.sent) parts.push(`${key.toUpperCase()} (${r.picks} picks → ${r.to})`);
        else if (r.skipped) problems.push(`${key.toUpperCase()}: ${r.reason}`);
        else if (r.error) problems.push(`${key.toUpperCase()}: ${r.error}`);
      }
      if (parts.length) toast.success(`Sent: ${parts.join(' + ')}`);
      else if (problems.length) toast.error(`Send failed — ${problems.join(' · ')}`);
      else toast.error('Send failed — check settings');
    } catch (e) {
      toast.error(`Send failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSending(null);
    }
  };

  useEffect(() => { load(); }, []);

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="w-full rounded-xl bg-gradient-to-r from-amber-500/10 to-red-500/10 border border-amber-500/30 px-3 py-2 text-left text-xs text-amber-300 hover:bg-amber-500/15"
        data-testid="lock-list-reopen"
      >
        <Flame className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
        Show weekend lock list (CFB blowouts + NFL favorites)
      </button>
    );
  }

  return (
    <div className="rounded-xl bg-gradient-to-br from-amber-500/5 to-red-500/5 border border-amber-500/30 p-3 space-y-2"
         data-testid="weekly-lock-list">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-bold text-amber-300 flex items-center gap-1.5">
            <Flame className="w-4 h-4" /> Weekend Lock List
          </h2>
          <p className="text-[10px] text-zinc-500">
            Odds-based ranking of the biggest favorites in the next 7 days.
            Build your parlay from the top rows.
          </p>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setEmailOpen(v => !v)}
                  className="text-xs text-zinc-400 hover:text-amber-300 inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-zinc-800"
                  data-testid="lock-list-email-toggle">
            <Mail className="w-3 h-3" />
            Email
          </button>
          <button onClick={load}
                  disabled={loading}
                  className="text-xs text-zinc-400 hover:text-amber-300 inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-zinc-800"
                  data-testid="lock-list-refresh">
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Refresh
          </button>
          <button onClick={() => setOpen(false)}
                  className="text-xs text-zinc-500 hover:text-zinc-300 px-2 py-1"
                  data-testid="lock-list-collapse">
            Hide
          </button>
        </div>
      </div>

      {emailOpen && (
        <div className="rounded-lg bg-zinc-950/80 border border-amber-500/40 p-3 space-y-2"
             data-testid="lock-list-email-panel">
          <p className="text-[11px] text-zinc-400 leading-snug">
            Delivery is scheduled: <span className="text-amber-300 font-semibold">CFB every Friday 7 PM PT</span> ·
            <span className="text-amber-300 font-semibold"> NFL every Saturday 7 PM PT</span>. Change the recipient
            or send one right now below.
          </p>
          <label className="block text-[10px] uppercase tracking-wider text-zinc-500">
            Recipient email
            <input
              type="email"
              value={settings.recipient_email || ''}
              onChange={(e) => setSettings({ ...settings, recipient_email: e.target.value })}
              placeholder="you@example.com"
              className="mt-1 w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-sm text-white focus:border-amber-500/60 focus:outline-none"
              data-testid="lock-list-email-input"
            />
          </label>
          <div className="flex items-center gap-3 text-[11px] text-zinc-400 pt-1">
            <label className="inline-flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={!!settings.cfb_enabled}
                     onChange={(e) => setSettings({ ...settings, cfb_enabled: e.target.checked })}
                     className="accent-amber-500"
                     data-testid="lock-list-cfb-enabled" />
              CFB email (Fri 7 PM)
            </label>
            <label className="inline-flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={!!settings.nfl_enabled}
                     onChange={(e) => setSettings({ ...settings, nfl_enabled: e.target.checked })}
                     className="accent-amber-500"
                     data-testid="lock-list-nfl-enabled" />
              NFL email (Sat 7 PM)
            </label>
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <button onClick={() => sendNow('cfb')}
                    disabled={sending !== null}
                    className="text-[11px] px-2.5 py-1 rounded border border-zinc-700 text-zinc-300 hover:border-amber-500 hover:text-amber-300 inline-flex items-center gap-1 disabled:opacity-50"
                    data-testid="lock-list-send-cfb">
              {sending === 'cfb' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              Send CFB now
            </button>
            <button onClick={() => sendNow('nfl')}
                    disabled={sending !== null}
                    className="text-[11px] px-2.5 py-1 rounded border border-zinc-700 text-zinc-300 hover:border-amber-500 hover:text-amber-300 inline-flex items-center gap-1 disabled:opacity-50"
                    data-testid="lock-list-send-nfl">
              {sending === 'nfl' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              Send NFL now
            </button>
            <button onClick={saveSettings}
                    className="text-[11px] px-2.5 py-1 rounded bg-amber-500 hover:bg-amber-400 text-black font-semibold"
                    data-testid="lock-list-save-settings">
              Save
            </button>
          </div>
        </div>
      )}

      {loading && !data ? (
        <div className="flex items-center justify-center py-6 text-zinc-500">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
        </div>
      ) : data ? (
        <div className="space-y-2">
          {only !== 'nfl' && <Section
            title="🏈 CFB Blowouts (Fri night / Sat)"
            icon={<Flame className="w-4 h-4 text-red-400" />}
            picks={data.cfb_blowouts || []}
            league="cfb"
          />}
          {only !== 'cfb' && <Section
            title="🏈 NFL Best Odds (Sun / Mon)"
            icon={<Target className="w-4 h-4 text-sky-400" />}
            picks={data.nfl_locks || []}
            league="nfl"
          />}
          <p className="text-[9px] text-zinc-600 italic px-1">
            Spread → win prob via logistic approximation. Not betting
            advice — ESPN public-JSON odds, provider is {data.cfb_blowouts?.[0]?.provider || data.nfl_locks?.[0]?.provider || 'DraftKings'}.
          </p>
        </div>
      ) : null}
    </div>
  );
}
